import React from 'react'
import { Form, Formik } from 'formik'
import { toFormikValidationSchema } from 'zod-formik-adapter'
import { DateTime } from 'luxon'
import { Box, Grid, Typography } from '@mui/material'
import { PatientHistoryType } from 'src/graphql.generated'
import { useGetFullPatientQuery } from 'src/participant/patientQueries.generated'
import useAddPatientHistoryEntry from 'src/participantHistory/useAddPatientHistoryEntry'
import { useToasts } from 'react-toast-notifications'
import DischargeSummaryNoteFields from 'src/participantHistory/DischargeSummaryNoteFields'
import { Loader } from 'src/designSystem'
import { DraftIndicator } from 'src/designSystem/DraftIndicator'
import SubmitButton from '../SubmitButton'
import { clearFormFromLocalStorage } from '../persistFormHelpers'
import { DISCHARGE_SUMMARY_NOTE_FORM_SCHEMA, DischargeSummaryNoteFormType } from './DischargeSummaryNoteFormSchema'
import FormPersistor from '../FormPersistor'
import { useLocalStorageWithPrefillValues } from '../useLocalStorageWithPrefillValues'

const FORM_TYPE = PatientHistoryType.DischargeSummaryNote

export const getDefaultValues = (): DischargeSummaryNoteFormType => ({
  date: DateTime.now().toFormat('yyyy-MM-dd'),
  dischargeDate: DateTime.now().toFormat('yyyy-MM-dd'),
  billingCodes: [],
  participant: {
    firstName: '',
    lastName: '',
    memberID: '',
  },
  assessingPractitioner: {
    name: '',
    licenseType: '',
  },
  treatmentPlan: {
    smartGoals: '',
    goalsMet: 'yes',
    details: '',
  },
  reasonForDischarge: {
    reason: 'programCompletion',
    details: '',
  },
})

const DischargeSummaryNoteForm = ({
  uid,
  afterSubmittedCallback,
}: {
  uid: string
  afterSubmittedCallback?: () => void
}) => {
  const addPatientHistoryEntry = useAddPatientHistoryEntry()
  const { addToast } = useToasts()
  const { data: patientData } = useGetFullPatientQuery({ variables: { uid } })

  const { values: initialValues, status } = useLocalStorageWithPrefillValues(uid, FORM_TYPE, getDefaultValues)

  if (status === 'loading') {
    return <Loader size="large" />
  }

  return (
    <Grid item>
      <Formik<DischargeSummaryNoteFormType>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={toFormikValidationSchema(DISCHARGE_SUMMARY_NOTE_FORM_SCHEMA)}
        onSubmit={async (values, { resetForm }) => {
          try {
            await addPatientHistoryEntry({
              uid,
              date: values.date,
              type: PatientHistoryType.DischargeSummaryNote,
              value: values,
            })
            resetForm({ values: initialValues })
            clearFormFromLocalStorage(uid, FORM_TYPE)
            afterSubmittedCallback?.()
          } catch (e) {
            addToast('Failed to submit discharge summary note', {
              appearance: 'error',
            })
          }
        }}
      >
        {({ isSubmitting, errors, dirty }) => (
          <Form>
            {(status === 'draft' || dirty) && <DraftIndicator dirty={dirty} />}
            <DischargeSummaryNoteFields credentialingHealthPlan={patientData?.patient?.credentialingHealthPlan} />

            <Box justifyContent="flex-end" display="flex" mt={4}>
              <SubmitButton isSubmitting={isSubmitting} />
            </Box>

            {Object.values(errors).filter((error) => error).length > 0 && (
              <Box mt={3}>
                <Typography variant="body1" color="error">
                  Fill in required fields.
                </Typography>
              </Box>
            )}
            <FormPersistor uid={uid} formType={FORM_TYPE} />
          </Form>
        )}
      </Formik>
    </Grid>
  )
}

export default DischargeSummaryNoteForm
